import React, { lazy, Suspense, useCallback, useRef, useState, useMemo } from 'react';
import { IDatasource, IGetRowsParams, GridApi, ColDef } from 'ag-grid-community';

const ReactJson = lazy(() => import('react-json-view').then(m => ({ default: m.default })));
import { IioDataGrid } from '../../components/IioDataGrid';
import type { AuditLogPage } from '../../hooks/useAuditLog';
import { useGenerateAuditReport } from '../../hooks/useAuditLog';
import { useProject } from '../../hooks/useProject';
import { hasPermission } from '../../types/project';
import { api } from '../../services/api';
import { showApiErrorToast } from '../../services/errorToast';
import { DownloadButton } from '../../components/DownloadButton';
import { Modal } from '../../components/Modal';

const LIMIT = 100;

function formatLocalDate(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

function ActionDetailsModal({ value, onClose }: { value: unknown; onClose: () => void }) {
  return (
    <Modal
      title="Json Viewer"
      icon="fas fa-code"
      onClose={onClose}
      bodyStyle={{ overflowX: 'auto' }}
      footer={<button type="button" className="btn btn-primary" onClick={onClose}>Ok</button>}
    >
      <Suspense fallback={<p>Loading…</p>}>
        <ReactJson
          src={value as object}
          name={false}
          collapsed={1}
          displayDataTypes={false}
          displayObjectSize={false}
          enableClipboard={false}
        />
      </Suspense>
    </Modal>
  );
}

function buildColumnDefs(onView: (v: unknown) => void): ColDef[] {
  return [
  {
    headerName: 'Date',
    field: 'date',
    colId: 'date',
    minWidth: 180,
    cellRenderer: ({ data }: any) => data ? formatLocalDate(data.date) : '',
  },
  {
    headerName: 'Email',
    field: 'user',
    minWidth: 200,
    filter: false,
    cellRenderer: ({ data }: any) => data ? data.user : '',
  },
  {
    headerName: 'IP Address',
    field: 'ipAddress',
    minWidth: 140,
    filter: false,
  },
  {
    headerName: 'Action',
    field: 'actionDescription',
    minWidth: 140,
    filter: false,
    cellRenderer: ({ data }: any) => {
      if (!data) return '';
      const span = document.createElement('span');
      span.className = 'label label-default';
      span.textContent = data.actionDescription;
      return span;
    },
  },
  {
    headerName: 'Action Details',
    field: 'actionDetails',
    filter: false,
    minWidth: 140,
    sortable: false,
    cellRenderer: ({ value }: any) => {
      if (!value) return '';
      const a = document.createElement('a');
      a.href = '#';
      a.textContent = 'View Details';
      a.addEventListener('click', e => { e.preventDefault(); onView(value); });
      return a;
    },
  },
  ];
}

export function AuditTab({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const gridApiRef = useRef<GridApi | null>(null);
  const hasManageReports = project ? hasPermission(project, 'MANAGE_REPORTS') : false;
  const [selectedDetails, setSelectedDetails] = useState<unknown>(null);
  const columnDefs = useMemo(() => buildColumnDefs(setSelectedDetails), []);

  const datasource: IDatasource = useCallback((): IDatasource => ({
    rowCount: undefined,
    getRows({ startRow, successCallback, failCallback }: IGetRowsParams) {
      const page = Math.floor(startRow / LIMIT);
      api.post<AuditLogPage>(
          `/auditing/project/${projectId}?limit=${LIMIT}&page=${page}`,
          [{ sortBy: 'date', sortDirection: 'DESC' }],
        )
        .then(data => successCallback(data.records, data.totalRecords))
        .catch((err) => { showApiErrorToast(err); failCallback(); });
    },
  }), [projectId])();

  const handleRefresh = () => {
    gridApiRef.current?.setDatasource(datasource);
  };

  const generateReport = useGenerateAuditReport(projectId);

  return (
    <>
      {selectedDetails !== null && (
        <ActionDetailsModal value={selectedDetails} onClose={() => setSelectedDetails(null)} />
      )}
      <div>
      {hasManageReports && (
        <DownloadButton onClick={() => generateReport.mutate()} disabled={generateReport.isPending} />
      )}

      <h2><i className="fas fa-info" aria-hidden="true" /> Project Audit</h2>
      <button className="btn btn-default" type="button" aria-label="Refresh" onClick={handleRefresh}>
        <i className="fas fa-sync-alt" aria-hidden="true" />
      </button>

      <IioDataGrid
        columnDefs={columnDefs}
        datasource={datasource}
        rowSelection="multiple"
        rowMultiSelectWithClick
        onGridReady={api => { gridApiRef.current = api; }}
      />
    </div>
    </>
  );
}
